import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, RiseIn, SplitWords } from "@/components/site/Reveal";
import EditorialMarquee from "@/components/site/Marquee";
import { DOCTOR, MANIFESTO, CREDENTIALS, APPOINTMENTS } from "@/lib/data";

const HERO_IMG =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_drvikas-appointments/artifacts/7x468g7e_vikas%20full.jpg";
const INSTR_IMG =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_drvikas-appointments/artifacts/6fb3nibn_ot.webp";
const CLINIC_IMG =
  "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] pt-32 pb-16 px-6 lg:px-12 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-6 items-end min-h-[80vh]">
        <div className="col-span-12 lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="h-px w-10 bg-navy-600/40" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-navy-600/70">
              Consultant Minimal Invasive Surgeon
            </span>
          </motion.div>

          <h1
            className="tracking-[-0.02em] text-navy-600 leading-[0.9] text-[clamp(2.5rem,6.5vw,6.5rem)] whitespace-nowrap"
            style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 500 }}
          >
            <MaskedLine delay={0.15}>Dr.&nbsp;Vikas</MaskedLine>
            {"\u00A0"}
            <MaskedLine delay={0.35} className="italic">
              Matai.
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 max-w-lg text-navy-600/80 text-sm md:text-base leading-relaxed"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-4">
              {DOCTOR.titleLong}
            </div>
            Thirteen years of general and minimal-invasive surgery — lasers,
            staplers, endoscopic interventions, hybrid techniques, laparoscopic
            approaches and radiology-guided procedures.
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/book"
              data-testid="hero-cta-book"
              className="group inline-flex items-center gap-3 bg-navy-600 text-beige-100 px-7 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-navy-700 transition-colors"
            >
              Reserve a consultation
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
            <Link
              to="/diseases"
              data-testid="hero-cta-conditions"
              className="link-underline text-[11px] uppercase tracking-[0.28em] text-navy-600"
            >
              Conditions we treat
            </Link>
          </motion.div>
        </div>

        <motion.div
          style={{ y, opacity }}
          className="col-span-12 lg:col-span-5 relative"
        >
          <div className="relative">
            <motion.div
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden tall-clip"
            >
              <img
                src={HERO_IMG}
                alt="Dr. Vikas Matai — portrait in clinical coat"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-600/20 via-transparent to-transparent" />
            </motion.div>
            <div className="absolute -left-4 top-6 vertical-text text-[10px] uppercase tracking-[0.28em] text-navy-600/60">
              Portrait · Est. Practice 2007
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom bar with quick stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="mx-auto max-w-[1600px] mt-40 lg:mt-56 grid grid-cols-2 gap-6 border-t border-navy-600/15 pt-8"
      >
        {[
          ["13+", "Years in practice"],
          ["1,500+", "Surgeries performed"],
        ].map(([n, l]) => (
          <div key={l}>
            <div className="font-serif text-3xl md:text-5xl text-navy-600 tracking-tight">
              {n}
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-navy-600/60">
              {l}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section
      className="px-6 lg:px-12 py-24 lg:py-32 bg-beige-100"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <RiseIn>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-6">
              A four-point manifesto
            </div>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-navy-600">
              <SplitWords text="How this practice is run." />
            </h2>
          </RiseIn>
        </div>
        <div className="col-span-12 lg:col-span-8 lg:pl-16">
          <ul className="divide-y divide-navy-600/15">
            {MANIFESTO.map((m, i) => (
              <RiseIn key={m.n} delay={i * 0.08}>
                <li className="grid grid-cols-12 gap-6 py-10">
                  <div className="col-span-2 chapter-num text-navy-600/40 text-3xl md:text-5xl">
                    {m.n}
                  </div>
                  <div className="col-span-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="font-serif text-3xl md:text-4xl text-navy-600 tracking-tight">
                      {m.title}
                    </div>
                    <p className="md:col-span-2 text-sm md:text-base leading-relaxed text-navy-600/75">
                      {m.body}
                    </p>
                  </div>
                </li>
              </RiseIn>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function PhotoSplit() {
  return (
    <section className="px-6 lg:px-12 py-24" data-testid="photo-split">
      <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8 items-center">
        <RiseIn className="col-span-12 lg:col-span-6 order-2 lg:order-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-6">
            Meet the future, laser.
          </div>
          <h3 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight text-navy-600">
            The principles are conventional. <br />
            <span className="italic text-navy-600/60">
              The approach is not.
            </span>
          </h3>
          <p className="mt-8 max-w-md text-navy-600/75 leading-relaxed">
            The tools sit where they always have — kidney trays, sponge holders,
            a Kocher and a Metzenbaum. What changes, case by case, is the
            reading of the tissue, the sequence of the steps, and the patience
            to slow down when speed would betray.
          </p>
        </RiseIn>
        <RiseIn className="col-span-12 lg:col-span-6 order-1 lg:order-2" y={60}>
          <div className="relative overflow-hidden">
              <img
                src={INSTR_IMG}
                alt="Dr. Vikas Matai in the operating theatre using a Lumenis laser system"
                className="w-full h-[70vh] object-cover"
              />
            <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.28em] text-beige-100/90 bg-navy-600/40 backdrop-blur px-3 py-1.5">
              Fig. 02
            </div>
          </div>
        </RiseIn>
      </div>
    </section>
  );
}

function Credentials() {
  return (
    <section
      className="px-6 lg:px-12 py-24 lg:py-32 bg-navy-600 text-beige-100"
      data-testid="credentials-section"
    >
      <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
            Training <br />
            <span className="italic text-brass-400">& affiliation.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-8 lg:pl-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-beige-100/50 mb-6">
            Education
          </div>
          <ul>
            {CREDENTIALS.map((c, i) => (
              <RiseIn key={c.year} delay={i * 0.06}>
                <li className="grid grid-cols-12 gap-4 py-6 border-t border-beige-100/15">
                  <div className="col-span-2 chapter-num text-brass-400 text-2xl">
                    {c.year}
                  </div>
                  <div className="col-span-6 font-serif text-xl md:text-2xl tracking-tight">
                    {c.title}
                  </div>
                  <div className="col-span-4 text-sm text-beige-100/70 self-end">
                    {c.inst}
                  </div>
                </li>
              </RiseIn>
            ))}
          </ul>

          <div className="text-[10px] uppercase tracking-[0.3em] text-beige-100/50 mt-16 mb-6">
            Experience
          </div>
          <ul>
            {APPOINTMENTS.map((a, i) => (
              <RiseIn key={a.title} delay={i * 0.06}>
                <li className="grid grid-cols-12 gap-4 py-6 border-t border-beige-100/15">
                  <div className="col-span-6 font-serif text-xl md:text-2xl tracking-tight">
                    {a.title}
                  </div>
                  <div className="col-span-4 text-sm text-beige-100/70 self-end">
                    {a.org}
                  </div>
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.24em] text-beige-100/60 self-end text-right">
                    {a.period}
                  </div>
                </li>
              </RiseIn>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Availability() {
  return (
    <section className="px-6 lg:px-12 py-24" data-testid="availability-section">
      <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8 items-stretch">
        <RiseIn className="col-span-12 lg:col-span-7">
          <div className="relative overflow-hidden h-full">
            <img
              src={CLINIC_IMG}
              alt="Clinic interior — waiting area"
              className="w-full h-[70vh] object-cover"
            />
          </div>
        </RiseIn>

        <RiseIn className="col-span-12 lg:col-span-5" delay={0.1}>
          <div className="h-full flex flex-col justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-6">
                Availability
              </div>
              <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-navy-600">
                A slot,<br />
                <span className="italic text-navy-600/60">not a queue.</span>
              </h2>
              <p className="mt-6 max-w-md text-navy-600/75 leading-relaxed">
                Consultations are scheduled — not walk-in. Choose a location,
                pick a time that suits, and confirm on WhatsApp in a single tap.
              </p>
              <ul className="mt-10 space-y-4 text-sm text-navy-600/80">
                <li className="grid grid-cols-3 gap-3 border-t border-navy-600/15 pt-4">
                  <span className="col-span-1 text-[10px] uppercase tracking-[0.24em] text-navy-600/60">Mon — Fri</span>
                  <span className="col-span-2">10:00 — 12:00 · Sadguru Clinic, Akota</span>
                </li>
                <li className="grid grid-cols-3 gap-3 border-t border-navy-600/15 pt-4">
                  <span className="col-span-1 text-[10px] uppercase tracking-[0.24em] text-navy-600/60">Mon — Fri</span>
                  <span className="col-span-2">17:00 — 19:00 · Welcare Hospital, Atladra</span>
                </li>
                <li className="grid grid-cols-3 gap-3 border-t border-b border-navy-600/15 py-4">
                  <span className="col-span-1 text-[10px] uppercase tracking-[0.24em] text-navy-600/60">Sat & Sun</span>
                  <span className="col-span-2">10:00 — 12:00 · Welcare Hospital, Atladra</span>
                </li>
              </ul>
            </div>

            <Link
              to="/book"
              data-testid="availability-cta"
              className="mt-10 group inline-flex items-center justify-between gap-3 border border-navy-600 text-navy-600 px-6 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-navy-600 hover:text-beige-100 transition-colors"
            >
              Book online now
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </RiseIn>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="pb-8" data-testid="about-page">
      <Hero />
      <EditorialMarquee />
      <Manifesto />
      <PhotoSplit />
      <Credentials />
      <Availability />
    </div>
  );
}
